import get from 'lodash/get';
import { getDeliveryMethodsAfterCheckingShippingBackup } from '../utils/deliveryMethod';
import {
    REMOVE_ORDER_INSTALLMENTS,
    RESET_ORDER,
    SET_ORDER_INSTALLMENTS,
    SET_ORDER_UPDATING,
    UPDATE_BRAINTREE_CLIENT_TOKEN,
    UPDATE_ORDER,
    UPDATE_ORDER_ATTENDEES,
    UPDATE_ORDER_EMAIL,
    UPDATE_ORDER_STATUS,
    UPDATE_REDIRECT_INSTRUCTIONS,
} from '../actions/order/constants';
import { SAVE_EVENT_SHIPPING_SETTINGS } from '../actions/event';
import { SAVE_WAITLIST_ENTRY } from '../actions/waitlist';
import { TAX_POLICIES_LOAD_COMPLETE } from '../actions/taxPolicies';
import { UPDATE_TAX_EXEMPTION_MESSAGE } from '../actions/taxExemptions';
import {
    UPDATE_AVAILABLE_PAYMENT_METHODS,
    SET_CAN_RETRY_SUBMIT,
    SET_HAS_VIRTUAL_INCENTIVE_ERRORS,
} from '../actions/constants';

const getAttendeesWithTicketDeliveryMethod = (
    attendees,
    shippingSettings = {},
) =>
    attendees.map(({ ticketClass = {}, ...otherProps }) => {
        const { deliveryMethods, ...otherTicketClassProps } = ticketClass;

        return {
            ticketClass: {
                ...otherTicketClassProps,
                deliveryMethods: getDeliveryMethodsAfterCheckingShippingBackup(
                    deliveryMethods,
                    shippingSettings,
                ),
            },
            ...otherProps,
        };
    });

export const orderReducer = (state = {}, action) => {
    switch (action.type) {
        case RESET_ORDER:
            return {};

        case UPDATE_ORDER:
            return {
                ...state,
                ...action.payload,
            };

        case UPDATE_ORDER_ATTENDEES:
            return {
                ...state,
                attendees: action.payload,
            };

        case UPDATE_ORDER_EMAIL:
            return {
                ...state,
                email: action.payload.email,
            };

        case UPDATE_ORDER_STATUS:
            return {
                ...state,
                status: action.payload,
            };

        case UPDATE_REDIRECT_INSTRUCTIONS:
            return {
                ...state,
                redirectInstructions: action.payload,
            };

        case UPDATE_AVAILABLE_PAYMENT_METHODS:
            return {
                ...state,
                availablePaymentMethods: action.payload.paymentOptions,
            };

        case UPDATE_BRAINTREE_CLIENT_TOKEN:
            if (action.payload) {
                return {
                    ...state,
                    braintreeClientToken: action.payload,
                };
            }
            return state;

        case SAVE_WAITLIST_ENTRY:
            return {
                ...state,
                waitlistEntry: action.payload,
            };

        case SET_ORDER_UPDATING:
            return {
                ...state,
                isUpdating: action.payload,
            };

        case SET_CAN_RETRY_SUBMIT:
            return {
                ...state,
                canRetrySubmit: action.payload,
            };

        case SET_ORDER_INSTALLMENTS:
            return {
                ...state,
                displayInstallments: action.payload,
            };

        case REMOVE_ORDER_INSTALLMENTS:
            return {
                ...state,
                displayInstallments: undefined,
            };
        case SAVE_EVENT_SHIPPING_SETTINGS:
            return {
                ...state,
                attendees: getAttendeesWithTicketDeliveryMethod(
                    state.attendees,
                    action.payload.shippingSettings,
                ),
            };
        case TAX_POLICIES_LOAD_COMPLETE:
            return {
                ...state,
                taxIdName: action.payload,
            };
        case UPDATE_TAX_EXEMPTION_MESSAGE:
            return {
                ...state,
                taxExemptionData: action.payload,
            };
        case SET_HAS_VIRTUAL_INCENTIVE_ERRORS:
            return {
                ...state,
                hasVirtualIncentivesErrors: action.payload,
            };

        default:
            return state;
    }
};

const getOrderId = (state) => state.id;
const getOrderCosts = (state) => state.costs;
const getRedirectInstructions = (state) => state.redirectInstructions;
const getAvailablePaymentMethods = (state) => state.availablePaymentMethods;
// This might look silly, but it is used also at checkoutWidget's actions/order.js
const hasMoreThanOnePaymentMethod = (orderState) =>
    get(orderState, ['availablePaymentMethods', 'length'], 0) > 1;

const haveAvailablePaymentMethods = (orderState) =>
    get(orderState, 'availablePaymentMethods', false);

const hasNoPaymentMethodsAvailable = (orderState) =>
    get(orderState, ['availablePaymentMethods', 'length'], 0) === 0;

const hasOnlyOnePaymentMethodAndInstrumentType = (orderState) => {
    // Easy case: more than one payment method == at least 1 instrument type per payment method, so > 1
    if (hasMoreThanOnePaymentMethod(orderState)) {
        return false;
    }
    // Another easy case: no methods available
    if (hasNoPaymentMethodsAvailable(orderState)) {
        return false;
    }

    // Last scenario: One payment method, so need to check how many instrument types has
    return orderState.availablePaymentMethods[0].instrumentTypes.length === 1;
};
// Again, used also at checkoutWidget's actions/order.js
// Careful: doesn't checks for non-existance of orderState
const getFirstAvailableInstrumentType = (orderState) =>
    orderState.availablePaymentMethods[0].instrumentTypes[0];

export const selectors = {
    getAvailablePaymentMethods,
    getFirstAvailableInstrumentType,
    getOrderCosts,
    getOrderId,
    getRedirectInstructions,
    hasMoreThanOnePaymentMethod,
    hasNoPaymentMethodsAvailable,
    haveAvailablePaymentMethods,
    hasOnlyOnePaymentMethodAndInstrumentType,
};
